/**
 * useCampaignPanelInfo Hook
 * Shared panel info computation for all campaign types.
 * Config-driven: resolves display name from campaign config registry.
 */

import { useMemo, useEffect } from 'react';
import { useIsPortrait } from 'shared/hooks/useIsPortrait';
import { useAppStore } from 'store/app/appStore';
import { buildResourceUrls } from 'core/api/buildResourceUrls';
import { getBackgroundImageUrl } from 'features/campaigns';
import { campaignConfigRegistry } from '../../../configs';
import { isCampaign, useCampaignActions } from '../../../store/campaignsStore';
import { MULTI_VIEW_MODE } from '../../../../../constants';
import { generateMockIntervals } from 'features/campaigns/mocks/campaignMockData';

import type { CampaignPanelInfo } from '../types';
import type { CampaignTypes, CampaignSource } from '../../../types/campaign.types';
import type ISectionSettings from 'types/models/ISectionSettings';

interface UseCampaignPanelInfoParams {
  campaignType: CampaignTypes;
  data: ISectionSettings;
  items: CampaignSource[];
}

export const useCampaignPanelInfo = ({
  campaignType,
  data,
  items,
}: UseCampaignPanelInfoParams): CampaignPanelInfo => {
  const isPortrait = useIsPortrait();
  const assetsHost = useAppStore((state) => state.settings.assetsHost);

  const config = campaignConfigRegistry[campaignType];
  const isSingleItem = items.length === 1;
  const activeCampaign = isSingleItem ? items[0] : null;
  // For multi-item panels: pick the first (sorted active-first) campaign as
  // representative for intervals and as a date fallback when dateRange is
  // absent from section settings. Using a mock here would freeze inside
  // useMemo and quickly expire ("Finished" after a few seconds away from the lobby).
  const representativeCampaign = activeCampaign ?? items[0] ?? null;

  const { setPanelInfo } = useCampaignActions();

  const panelInfo = useMemo(() => {
    const rawResources = activeCampaign
      ? activeCampaign?.resources
      : data.context?.templateInstance?.resourcesRelative
        ?? data.context?.templateInstance?.resources;

    // Section (`templateInstance`) assets arrive as relative
    // `/resourceservice/...` paths — now under `resourcesRelative`, falling
    // back to the legacy `resources` for backends that haven't migrated.
    // `buildResourceUrls` in the campaigns/tournaments endpoints only rewrites
    // `item.resources`, never the nested `templateInstance` ones, so these
    // never got the host prepended. Apply the same host-only rewrite here so
    // they resolve to the real file instead of being mistaken for bundled
    // `/assets/portal` assets.
    const resources = rawResources
      ? buildResourceUrls({ resources: rawResources }, assetsHost).resources
      : undefined;

    const { backgroundImage, backgroundOverlayImage, backgroundOverlayLogo } = resources
      ? getBackgroundImageUrl({
          resources,
          isPortrait,
          isCollapsed: false,
          campaignType,
          viewMode: MULTI_VIEW_MODE,
          hasMany: !isSingleItem,
          isPanel: true,
        })
      : { backgroundImage: '', backgroundOverlayImage: '', backgroundOverlayLogo: '' };

    const dateRange = data.context?.dateRange;
    const dateRangeStartDate = dateRange ? dateRange[0] : undefined;
    const dateRangeEndDate = dateRange ? dateRange[1] : undefined;

    // Single campaign: campaign's own date takes priority, section settings as fallback.
    // Multiple campaigns: section settings dateRange takes priority, first campaign as fallback.
    const startDate = isSingleItem
      ? (representativeCampaign?.startDate ?? dateRangeStartDate)
      : (dateRangeStartDate || representativeCampaign?.startDate);
    // Same priority logic as startDate.
    const endDate = isSingleItem
      ? (representativeCampaign?.endDate ?? dateRangeEndDate)
      : (dateRangeEndDate || representativeCampaign?.endDate);

    return {
      backgroundImage,
      backgroundOverlayImage,
      backgroundOverlayLogo: backgroundOverlayLogo || '',
      backgroundColor: undefined,
      rangeBackground: '',
      prizeFund: isSingleItem
        ? String(activeCampaign?.prizeFund || '')
        : String(data.context?.prizeFund || ''),
      prizeCurrency: isSingleItem
        ? activeCampaign?.baseCurrency || ''
        : data.context?.prizeCurrency || '',
      headerTitle: isSingleItem
        ? activeCampaign?.name || config.displayName
        : data.context?.headerTitle || config.displayName,
      startDate,
      endDate,
      intervals: representativeCampaign && isCampaign(representativeCampaign)
        ? representativeCampaign.intervals
        : generateMockIntervals(),
      joined: activeCampaign?.joined ?? false,
      isSingleItem,
      type: campaignType,
    };
  }, [data.context, activeCampaign, representativeCampaign, isSingleItem, isPortrait, assetsHost, campaignType, config.displayName]);

  useEffect(() => {
    setPanelInfo(panelInfo, campaignType);
  }, [panelInfo, campaignType, setPanelInfo]);

  return panelInfo;
};
